"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.areasToChooseAgain = exports.areasMissingReason = exports.assessedSurfaceHours = exports.pmHours = exports.measuredHoursOf = exports.APPROVED_REASON_MAX = exports.APPROVED_SOURCES = void 0;
exports.assessedPricedHours = assessedPricedHours;
exports.approvedSourceOf = approvedSourceOf;
exports.fixedAssessed = fixedAssessed;
exports.pricedApprovedHours = pricedApprovedHours;
exports.approvedHoursOf = approvedHoursOf;
exports.approvedChange = approvedChange;
exports.withApprovedChoice = withApprovedChoice;
exports.approvedSummary = approvedSummary;
// Which hours price an area (PRICING.md "Which hours are priced", DD 2026-10-06/26/27): the
// review chooses Measured, Assessed or Other per area, and that choice is the approved
// hours. Measured is the default and follows the measurements. Assessed fixes the PM's hours
// per surface when chosen, and every other surface prices at its own hours in each option, extra
// paint systems included (#607). Other is a figure fixed when chosen. So a PM's later change never
// moves the price by itself. Hours are per building and before down time, like measured hours. Pure.
const measure_ts_1 = require("./measure.js");
const surfaces_ts_1 = require("./surfaces.js");
/** hig_approvedhourssource, in CRM's order (schema/m2q-schema.json). */
exports.APPROVED_SOURCES = ['Measured', 'Assessed', 'Other'];
/** hig_approvedhours is a 2-decimal column. */
const HOURS_PLACES = 100;
const stored = (hours) => Math.round(hours * HOURS_PLACES) / HOURS_PLACES;
/** The longest reason the area's document keeps for Other hours. */
exports.APPROVED_REASON_MAX = 500;
/** A surface's measured hours on its own (its group's count applied). */
const measuredHoursOf = (doc, types) => (surface) => (0, measure_ts_1.surfaceTotalsIn)(doc, surface, types).hours;
exports.measuredHoursOf = measuredHoursOf;
/** The PM's hours by surface id, as entered now. */
const pmHours = (doc) => Object.fromEntries(doc.surfaces.flatMap((s) => (typeof s.assessedHours === 'number' ? [[s.id, s.assessedHours]] : [])));
exports.pmHours = pmHours;
/** One surface under Assessed: the PM's hours from `pm` where it has some, else its own (#607: extra paint systems included). */
const assessedSurfaceHours = (pm, surface, own) => Object.hasOwn(pm, surface.id) ? pm[surface.id] : own(surface);
exports.assessedSurfaceHours = assessedSurfaceHours;
const assessedOver = (pm, surfaces, own) => surfaces.reduce((sum, s) => sum + (0, exports.assessedSurfaceHours)(pm, s, own), 0);
/** What Assessed prices with the PM's hours as they are now, measured for the rest (the REVIEW's "Assessed (PM)"). Null when nothing is assessed. */
function assessedPricedHours(doc, types) {
    if (!doc.surfaces.some((s) => typeof s.assessedHours === 'number'))
        return null;
    return assessedOver((0, exports.pmHours)(doc), doc.surfaces, (0, exports.measuredHoursOf)(doc, types));
}
/** The area's chosen source; a figure stored with no source (set before the choice existed) counts as Other. Null = not chosen. */
function approvedSourceOf(fields) {
    const source = fields.hig_approvedhourssource;
    if (exports.APPROVED_SOURCES.includes(source))
        return source;
    return typeof fields.hig_approvedhours === 'number' ? 'Other' : null;
}
/** The PM's hours the REVIEW fixed for Assessed; null when there are none (an empty set is never a valid Assessed choice). */
function fixedAssessed(doc) {
    const fixed = doc.approvedAssessed;
    return fixed && Object.keys(fixed).length > 0 ? fixed : null;
}
/**
 * The hours one option prices the area from in place of its surfaces' own: null = its own (Measured, or nothing chosen).
 * Assessed: the PM's hours fixed at the choice for those surfaces, and `own` for the rest of `kept` (the surfaces the option
 * keeps). Other: the typed figure. Assessed with no fixed PM hours (chosen before they were fixed per surface, or with
 * none) keeps its stored figure; Accept, the proposal and Issue refuse it until it is chosen again (areasToChooseAgain).
 */
function pricedApprovedHours(fields, doc, kept, own) {
    const source = approvedSourceOf(fields);
    const figure = typeof fields.hig_approvedhours === 'number' ? fields.hig_approvedhours : null;
    const fixed = fixedAssessed(doc);
    if (source === 'Assessed' && fixed)
        return assessedOver(fixed, kept, own);
    return source && source !== 'Measured' ? figure : null;
}
/** The same surfaces with the same hours (to the stored 2 decimals). */
function sameHours(a, b) {
    const ids = Object.keys(a);
    return ids.length === Object.keys(b).length && ids.every((id) => Object.hasOwn(b, id) && stored(a[id]) === stored(b[id]));
}
/** The area's approved hours as the review shows them; `option` = as that option prices them (else measured for every surface). */
function approvedHoursOf(fields, doc, types, option) {
    const chosenSource = approvedSourceOf(fields);
    const measured = (0, measure_ts_1.areaHours)(doc, types);
    const assessed = assessedPricedHours(doc, types);
    const priced = pricedApprovedHours(fields, doc, option?.kept ?? doc.surfaces, option?.own ?? (0, exports.measuredHoursOf)(doc, types));
    const fixed = fixedAssessed(doc);
    return {
        source: chosenSource ?? 'Measured',
        chosen: chosenSource !== null,
        measured,
        assessed,
        hours: priced ?? measured,
        changed: chosenSource === 'Assessed' && fixed !== null && !sameHours(fixed, (0, exports.pmHours)(doc)),
        stale: chosenSource === 'Assessed' && fixed === null,
        needsChoice: chosenSource === null && assessed !== null,
        reason: doc.approvedReason ?? '',
        missingReason: chosenSource === 'Other' && !(doc.approvedReason ?? '').trim(),
    };
}
/**
 * The area's columns for a choice (from approvedHoursOf): Measured stores no figure (it follows the measurements); Assessed
 * stores today's assessed figure (measured when nothing is assessed), which CRM's job lists total; Other the typed hours,
 * measured until some are typed. What Assessed prices is the PM's hours fixed in the surfaces document (withApprovedChoice).
 */
function approvedChange(source, area, otherHours) {
    const hours = source === 'Measured' ? null : stored(source === 'Assessed' ? (area.assessed ?? area.measured) : (otherHours ?? area.measured));
    return { hig_approvedhourssource: source, hig_approvedhours: hours };
}
/**
 * The area's surfaces document for a choice: Assessed fixes the PM's hours as they are in it now (none fixes nothing, so
 * the area must be chosen again: the desk offers Assessed only with PM hours); Other keeps its reason; a choice clears what it doesn't use.
 */
function withApprovedChoice(doc, source, reason) {
    const pm = (0, exports.pmHours)(doc);
    return (0, surfaces_ts_1.setApprovedAssessed)((0, surfaces_ts_1.setApprovedReason)(doc, source === 'Other' ? reason : ''), source === 'Assessed' && Object.keys(pm).length > 0 ? pm : null);
}
/** The areas this option prices whose approved hours hold it back (left-out and wash-only areas don't price their hours). */
const heldBack = (info, flag) => [...info].filter((area) => area.hours && flag(area.hours) && !area.excluded && !area.washOnly).map((area) => area.name);
/** Areas this option prices from Other hours with no reason. */
const areasMissingReason = (info) => heldBack(info, (h) => h.missingReason);
exports.areasMissingReason = areasMissingReason;
/** Areas whose Assessed was chosen before the PM's hours were fixed per surface (#607): chosen again before Accept, proposal or Issue. */
const areasToChooseAgain = (info) => heldBack(info, (h) => h.stale);
exports.areasToChooseAgain = areasToChooseAgain;
/** The areas whose PM hours wait for a choice, and how the job's areas are priced: "4 measured · 2 assessed · 1 other". */
function approvedSummary(areas) {
    const counts = exports.APPROVED_SOURCES.map((source) => [source, areas.filter((a) => a.source === source).length]).filter(([, n]) => n > 0);
    return { waiting: areas.filter((a) => a.needsChoice).length, line: counts.map(([source, n]) => `${n} ${source.toLowerCase()}`).join(' · ') };
}
